import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { Store, BadgeCheck, ArrowLeft, Heart, ShoppingCart, Truck, ChevronLeft, ChevronRight, X, ZoomIn, MapPin, User, Share2 } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { addGuestItem, guestCartCount } from "@/lib/guestCart";
import { Logo } from "@/components/Logo";
import ShopHeader from "@/components/ShopHeader";
import { Stars } from "@/components/reviews/Stars";
import ReviewsSection from "@/components/reviews/ReviewsSection";
import ReviewSummary from "@/components/ReviewSummary";
import RecoRail from "@/components/RecoRail";
import { toast } from "sonner";

const setMeta = (n, c) => { let e = document.head.querySelector(`meta[name="${n}"]`); if (!e) { e = document.createElement("meta"); e.name = n; document.head.appendChild(e); } e.content = c || ""; };

export default function ProductPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [sp, setSp] = useSearchParams();
  const [p, setP] = useState(undefined);
  const [loc, setLoc] = useState(null);
  const [editingLoc, setEditingLoc] = useState(false);
  const [locForm, setLocForm] = useState({ state: "", district: "", pincode: "" });
  const [qty, setQty] = useState(1);
  const [descExpanded, setDescExpanded] = useState(false);
  const [vsel, setVsel] = useState(null);
  const [pickedImg, setPickedImg] = useState(null);
  const [lightbox, setLightbox] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [custReq, setCustReq] = useState("");
  const [custFiles, setCustFiles] = useState([]);
  const uploadCustFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) { toast.error("Use JPG, PNG or WebP"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large. Maximum allowed size is 10 MB per image."); return; }
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/marketplace/product/image/upload", fd); setCustFiles((f) => [...f, { name: file.name, url: r.data.url }]); toast.success("Attached"); }
    catch { toast.error("Upload failed"); }
  };
  const submitCustom = async () => {
    if (!custReq.trim()) { toast.error("Describe what you'd like"); return; }
    try { const r = await api.post("/marketplace/custom/request", { product_id: id, requirements: custReq, files: custFiles }); toast.success(`Request ${r.data.number} sent — you'll pay only after the seller confirms the price`); setShowCustom(false); setCustReq(""); setCustFiles([]); nav("/app/custom-orders"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Please log in to request"); }
  };

  useEffect(() => {
    api.post(`/marketplace/recently-viewed/${id}`).catch(() => {});
    const doFetch = (l) => {
      const qs = l && (l.state || l.district || l.pincode) ? `?state=${encodeURIComponent(l.state || "")}&district=${encodeURIComponent(l.district || "")}&pincode=${encodeURIComponent(l.pincode || "")}` : "";
      api.get(`/marketplace/public/product/${id}${qs}`).then((r) => setP(r.data.product)).catch(() => setP(null));
    };
    doFetch(null); // load the product immediately; the location lookup below only refines serviceability
    api.get("/marketplace/location").then((r) => {
      const l = r.data.location;
      if (l && (l.state || l.district || l.pincode)) {
        setLoc(l); setLocForm({ state: l.state || "", district: l.district || "", pincode: l.pincode || "" }); doFetch(l);
      } else {
        api.get("/marketplace/addresses").then((ar) => {
          const list = ar.data.addresses || [];
          const def = list.find((a) => a.is_default) || list[0];
          if (def && (def.pincode || def.state)) {
            const dl = { country: "IN", state: def.state || "", district: def.district || "", pincode: def.pincode || "" };
            setLoc(dl); setLocForm({ state: dl.state, district: dl.district, pincode: dl.pincode }); doFetch(dl);
          }
        }).catch(() => {});
      }
    }).catch(() => {});
  }, [id]);
  useEffect(() => {
    if (p) {
      document.title = `${p.name} — Suffle Online`;
      setMeta("description", (p.description || p.name || "").slice(0, 160));
      // Structured data (JSON-LD) from REAL product data only — no fabricated price/rating.
      const rs = p.rating_summary || {};
      const ld = {
        "@context": "https://schema.org", "@type": "Product",
        name: p.name, ...(p.brand ? { brand: { "@type": "Brand", name: p.brand } } : {}),
        ...(p.image ? { image: p.image } : {}),
        ...(p.description ? { description: p.description } : {}),
        ...(p.price ? { offers: { "@type": "Offer", priceCurrency: "INR", price: String(p.price),
          availability: p.in_stock === false ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          ...(p.seller?.name ? { seller: { "@type": "Organization", name: p.seller.name } } : {}) } } : {}),
        ...(rs.rating_count > 0 ? { aggregateRating: { "@type": "AggregateRating",
          ratingValue: String(rs.rating_avg), reviewCount: String(rs.rating_count) } } : {}),
      };
      let el = document.getElementById("product-jsonld");
      if (!el) { el = document.createElement("script"); el.type = "application/ld+json"; el.id = "product-jsonld"; document.head.appendChild(el); }
      el.textContent = JSON.stringify(ld);
    }
    return () => { const e = document.getElementById("product-jsonld"); if (e) e.remove(); };
  }, [p]);

  useEffect(() => {
    // Tidy the address bar: swap an ID-based URL for the readable slug, preserving ?v=.
    if (p && p.slug && p.slug !== id) {
      const v = sp.get("v");
      nav(`/product/${p.slug}${v ? `?v=${v}` : ""}`, { replace: true });
    }
  }, [p]);

  useEffect(() => {
    setPickedImg(null);    if (p?.variants?.length) {
      const want = sp.get("v");
      const byParam = want && p.variants.find((v) => v.vid === want && (v.stock || 0) > 0);
      const f = byParam || p.variants.find((v) => (v.stock || 0) > 0) || p.variants[0];
      setVsel(f.vid);
    } else setVsel(null);
  }, [p]);

  useEffect(() => {
    const upd = () => {
      if (user) api.get("/marketplace/cart").then((r) => setCartCount(r.data.count ?? (r.data.items || []).reduce((n, i) => n + (i.qty || 0), 0))).catch(() => {});
      else setCartCount(guestCartCount());
    };
    upd();
    window.addEventListener("cart-updated", upd);
    return () => window.removeEventListener("cart-updated", upd);
  }, [user]);

  useEffect(() => {
    const onLoc = () => api.get("/marketplace/location").then((r) => {
      const l = r.data.location;
      if (l && (l.state || l.district || l.pincode)) { setLoc(l); setLocForm({ state: l.state || "", district: l.district || "", pincode: l.pincode || "" }); refetchProduct(l); }
    }).catch(() => {});
    window.addEventListener("location-updated", onLoc);
    return () => window.removeEventListener("location-updated", onLoc);
  }, [id]);

  const wish = async () => { try { await api.post(`/marketplace/wishlist/${id}`); toast.success("Added to wishlist"); } catch { toast.error("Please log in to save items"); } };
  const refetchProduct = (l) => {
    const qs = l && (l.state || l.district || l.pincode) ? `?state=${encodeURIComponent(l.state || "")}&district=${encodeURIComponent(l.district || "")}&pincode=${encodeURIComponent(l.pincode || "")}` : "";
    api.get(`/marketplace/public/product/${id}${qs}`).then((r) => setP(r.data.product)).catch(() => {});
  };
  const saveLoc = async () => {
    const l = { country: "IN", state: locForm.state.trim(), district: locForm.district.trim(), pincode: locForm.pincode.trim() };
    setLoc(l); setEditingLoc(false); refetchProduct(l);
    try { await api.post("/marketplace/location", l); } catch { /* guests: applies on this page only */ }
    toast.success("Delivery location updated");
  };
  const lookupPincode = async (pin) => {
    if (!/^\d{6}$/.test(pin)) return;
    try { const r = await api.get(`/geo/pincode/${pin}`); if (r.data?.valid) setLocForm((f) => ({ ...f, pincode: pin, state: r.data.state || f.state, district: r.data.district || f.district })); } catch { /* keep manual entry */ }
  };
  const addToCart = async (goCheckout) => {
    if (!user) {
      if (goCheckout) { toast.message("Please log in to continue to checkout"); nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`); return; }
      addGuestItem(id, qty, vsel);
      toast.success(`Added ${qty} to cart`);
      return;
    }
    if (goCheckout && user.email_verified === false) { toast.error("Please verify your email before placing an order."); nav("/app/profile"); return; }
    try { await api.post("/marketplace/cart/add", { product_id: id, qty, variant_vid: vsel }); window.dispatchEvent(new Event("cart-updated")); if (goCheckout) { nav("/app/checkout"); } else { toast.success(`Added ${qty} to cart`); } }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add to cart"); }
  };

  const shareProduct = async () => {
    const url = window.location.href;
    const title = p?.name ? `${p.name} — Suffle Online` : "Check this out on Suffle Online";
    try {
      if (navigator.share) await navigator.share({ title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied to clipboard"); }
    } catch { /* share dismissed */ }
  };

  if (p === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#0b0a1f]"><Logo variant="lg" /></div>;
  if (p === null) return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 p-6 text-center" data-testid="product-not-found">
      <Store className="h-10 w-10 text-slate-300" /><h1 className="text-xl font-bold text-slate-800">Product not available</h1>
      <Link to="/shop" className="rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white">Back to Shop</Link>
    </div>
  );

  const svc = p.serviceability || {};
  const locQS = loc && (loc.state || loc.district || loc.pincode) ? `?state=${encodeURIComponent(loc.state || "")}&district=${encodeURIComponent(loc.district || "")}&pincode=${encodeURIComponent(loc.pincode || "")}` : "";
  const selVar = (p.variants || []).find((v) => v.vid === vsel);
  const trackedStock = selVar ? selVar.stock : p.stock;
  const maxQty = (typeof trackedStock === "number" && trackedStock > 0) ? trackedStock : 10;
  const hasLoc = !!(loc && (loc.state || loc.district || loc.pincode));
  const canBuy = hasLoc && svc.available;
  const lbGallery = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
  const lbSv = (p.variants || []).find((v) => v.vid === vsel);
  const lbHero = pickedImg || (lbSv && lbSv.image) || lbGallery[0];
  const lbIdx = Math.max(0, lbGallery.indexOf(lbHero));
  const lbStep = (d) => { if (lbGallery.length > 1) setPickedImg(lbGallery[(lbIdx + d + lbGallery.length) % lbGallery.length]); };
  return (
    <div className="min-h-screen bg-slate-50" data-testid="product-page">
      <ShopHeader />
      <main className="mx-auto grid max-w-5xl gap-8 px-6 py-8 md:grid-cols-2">
        <div>
          {(() => {
            const sv = (p.variants || []).find((v) => v.vid === vsel);
            const gallery = (p.images && p.images.length) ? p.images : (p.image ? [p.image] : []);
            const heroImg = pickedImg || (sv && sv.image) || gallery[0];
            const curIdx = Math.max(0, gallery.indexOf(heroImg));
            const step = (d) => { if (gallery.length > 1) setPickedImg(gallery[(curIdx + d + gallery.length) % gallery.length]); };
            return (
              <>
                <div className="group relative aspect-square overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200">
                  {heroImg
                    ? <img src={heroImg} alt={p.name} onClick={() => setLightbox(true)} className="h-full w-full cursor-zoom-in bg-slate-50 object-contain" data-testid="product-hero-image" />
                    : <div className="flex h-full items-center justify-center"><Store className="h-12 w-12 text-slate-300" /></div>}
                  {heroImg && (
                    <button type="button" onClick={() => setLightbox(true)} data-testid="product-hero-zoom" className="absolute right-3 top-3 rounded-full bg-black/50 p-2 text-white opacity-0 transition group-hover:opacity-100" title="Zoom"><ZoomIn className="h-4 w-4" /></button>
                  )}
                  {gallery.length > 1 && (
                    <>
                      <button type="button" onClick={() => step(-1)} data-testid="product-hero-prev" className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-slate-700 shadow ring-1 ring-slate-200 transition hover:bg-white" title="Previous image"><ChevronLeft className="h-5 w-5" /></button>
                      <button type="button" onClick={() => step(1)} data-testid="product-hero-next" className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/80 p-2 text-slate-700 shadow ring-1 ring-slate-200 transition hover:bg-white" title="Next image"><ChevronRight className="h-5 w-5" /></button>
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-2 py-0.5 text-[11px] font-medium text-white" data-testid="product-hero-counter">{curIdx + 1} / {gallery.length}</div>
                    </>
                  )}
                </div>
                {gallery.length > 1 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="product-thumbnails">
                    {gallery.map((img, i) => {
                      const active = heroImg === img;
                      return (
                        <button key={`${img}-${i}`} type="button" onClick={() => setPickedImg(img)} data-testid={`product-thumb-${i}`}
                          className={`h-16 w-16 overflow-hidden rounded-lg bg-white ring-2 transition ${active ? "ring-blue-500" : "ring-slate-200 hover:ring-slate-300"}`}>
                          <img src={img} alt={`${p.name} ${i + 1}`} className="h-full w-full object-cover" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="product-name">{p.name}</h1>
          {p.brand && <p className="text-sm text-slate-500">{p.brand}</p>}
          {p.rating_summary?.rating_count > 0 && (
            <button onClick={() => document.querySelector('[data-testid="reviews-section"]')?.scrollIntoView({ behavior: "smooth" })}
              className="mt-1.5 inline-flex items-center gap-1.5 text-sm" data-testid="product-rating-badge">
              <Stars value={p.rating_summary.rating_avg} size={15} />
              <span className="font-semibold text-slate-700">{p.rating_summary.rating_avg}</span>
              <span className="text-slate-400">({p.rating_summary.rating_count} review{p.rating_summary.rating_count > 1 ? "s" : ""})</span>
            </button>
          )}
          <div className="mt-3 flex items-baseline gap-2">
            {(() => { const sv = (p.variants || []).find((v) => v.vid === vsel); const price = sv ? sv.price : p.price; const mrp = sv ? sv.mrp : p.mrp; const off = mrp > price ? Math.round((mrp - price) / mrp * 100) : p.discount; return (<>
              <span className="text-2xl font-bold text-slate-900" data-testid="product-price">₹{price}</span>
              {mrp > price && <><span className="text-sm text-slate-400 line-through">₹{mrp}</span><span className="text-sm font-semibold text-emerald-600">{off}% off</span></>}
            </>); })()}
          </div>
          {p.variants?.length > 0 && (
            <div className="mt-3" data-testid="variant-selector">
              <p className="mb-1.5 text-xs font-medium text-slate-500">Choose an option</p>
              <div className="flex flex-wrap gap-2">
                {p.variants.map((v) => { const oos = (v.stock || 0) <= 0; return (
                  <button key={v.vid} onClick={() => { if (!oos) { setVsel(v.vid); setPickedImg(null); setSp((prev) => { const n = new URLSearchParams(prev); n.set("v", v.vid); return n; }, { replace: true }); const cap = (typeof v.stock === "number" && v.stock > 0) ? v.stock : 10; setQty((q) => Math.min(q, cap)); } }} disabled={oos} data-testid={`variant-${v.vid}`} aria-pressed={vsel === v.vid} data-selected={vsel === v.vid}
                    className={`rounded-full border px-3 py-1.5 text-sm ${vsel === v.vid ? "border-blue-500 bg-blue-50 font-semibold text-blue-700" : "border-slate-200 text-slate-600"} ${oos ? "cursor-not-allowed line-through opacity-40" : "hover:border-blue-300"}`}>
                    {v.label || v.sku || "Option"}{oos ? " (out of stock)" : ""}
                  </button>); })}
              </div>
            </div>
          )}
          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-sm">
            Sold by <b>{p.seller?.name || "Seller"}</b>
            {p.seller?.verified && <span className="ml-1 inline-flex items-center gap-1 text-emerald-600"><BadgeCheck className="h-3.5 w-3.5" /> Verified Seller</span>}
            {p.seller?.slug && <Link to={`/${p.seller.slug}`} className="ml-2 text-blue-600 underline">View store</Link>}
          </div>
          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid="product-deliver-to">
            <div className="flex flex-wrap items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-500" />
              {hasLoc
                ? <><span className="text-slate-500">Deliver to:</span><span className="font-semibold text-slate-800" data-testid="product-deliver-to-label">{[loc.district, loc.state, loc.pincode].filter(Boolean).join(", ")}</span></>
                : <span className="text-slate-600" data-testid="product-deliver-to-empty">Add a delivery location to check availability</span>}
              <button onClick={() => setEditingLoc((v) => !v)} data-testid="product-change-location-btn" className="ml-auto rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:border-blue-300">{hasLoc ? "Change" : "Add location"}</button>
            </div>
            {editingLoc && (
              <div className="mt-2 grid gap-2 sm:grid-cols-4" data-testid="product-location-editor">
                <Input value={locForm.state} onChange={(e) => setLocForm({ ...locForm, state: e.target.value })} placeholder="State" data-testid="product-loc-state" />
                <Input value={locForm.district} onChange={(e) => setLocForm({ ...locForm, district: e.target.value })} placeholder="District" data-testid="product-loc-district" />
                <Input value={locForm.pincode} inputMode="numeric" maxLength={6} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, "").slice(0, 6); setLocForm((f) => ({ ...f, pincode: v })); if (v.length === 6) lookupPincode(v); }} placeholder="Pincode" data-testid="product-loc-pincode" />
                <button onClick={saveLoc} data-testid="product-loc-save" className="rounded-lg brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">Set</button>
                <p className="text-xs text-slate-400 sm:col-span-4" data-testid="product-loc-hint">Tip: enter your 6-digit PIN to auto-fill state &amp; district.</p>
              </div>
            )}
            <p className={`mt-2 text-sm ${canBuy ? "text-emerald-600" : "text-amber-600"}`} data-testid="product-serviceability">
              {!hasLoc ? "Add a delivery location to check availability"
                : svc.available ? "✓ Available for your selected location"
                : svc.reason === "out_of_area" ? "Not available for delivery to this location"
                : svc.reason === "out_of_stock" ? "Currently out of stock"
                : (svc.reason === "seller_not_approved" || svc.reason === "seller_suspended") ? "This store isn't active on the marketplace yet"
                : (svc.message || "Not available for delivery to this location")}
            </p>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center rounded-full border border-slate-200" data-testid="qty-control">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} data-testid="qty-decrement" className="px-3.5 py-3 text-lg leading-none text-slate-600 transition disabled:opacity-30" aria-label="Decrease quantity">−</button>
              <span className="w-8 text-center text-sm font-semibold text-slate-800" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(maxQty, q + 1))} disabled={qty >= maxQty} data-testid="qty-increment" className="px-3.5 py-3 text-lg leading-none text-slate-600 transition disabled:opacity-30" aria-label="Increase quantity">+</button>
            </div>
            <button onClick={() => addToCart(false)} data-testid="add-to-cart-btn" className="rounded-full brand-gradient-bg px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"><ShoppingCart className="mr-1 inline h-4 w-4" /> Add to Cart</button>
            <button onClick={() => addToCart(true)} disabled={!canBuy} data-testid="buy-now-btn" className="rounded-full border-2 border-blue-500 px-6 py-3 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-40">Buy Now</button>
            <button onClick={wish} data-testid="wishlist-btn" className="rounded-full border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600 hover:border-rose-300 hover:text-rose-600"><Heart className="h-4 w-4" /></button>
            <button onClick={shareProduct} data-testid="share-product-btn" title="Share this product" className="rounded-full border border-slate-200 px-4 py-3 text-sm font-medium text-slate-600 hover:border-blue-300 hover:text-blue-600"><Share2 className="h-4 w-4" /></button>
          </div>
          {p.customizable && (
            <button onClick={() => setShowCustom(true)} data-testid="request-customization-btn" className="mt-2 rounded-full border-2 border-violet-500 px-5 py-2.5 text-sm font-semibold text-violet-600 hover:bg-violet-50">✨ Request customization — pay only after the seller confirms the price</button>
          )}
          {!user && (
            <p className="mt-2 text-xs text-slate-500" data-testid="guest-save-cart-hint">Shopping as a guest — <button onClick={() => nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`)} className="font-semibold text-blue-600 hover:underline">log in</button> to save your cart. Your items carry over when you sign in.</p>
          )}
          {p.processing_time && <p className="mt-3 text-xs text-slate-400">{p.processing_time}</p>}
          {p.delivery_estimate && <p className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-600" data-testid="product-delivery-estimate"><Truck className="h-3.5 w-3.5 text-slate-400" />{p.delivery_estimate}</p>}
          {p.return_policy && (p.return_policy.non_returnable
            ? <span className="mt-2 inline-block rounded-full bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-600" data-testid="product-return-badge">Non-returnable</span>
            : <span className="mt-2 inline-block rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700" data-testid="product-return-badge">
                {p.return_policy.return_type === "return_replace" ? `${p.return_policy.window_days}-day return & replace`
                  : p.return_policy.return_type === "replaceable" ? `${p.return_policy.window_days}-day replacement`
                  : `${p.return_policy.window_days}-day returns`}
              </span>)}
          {(p.attributes?.food_type || p.food_type) && (
            <span data-testid="product-food-badge" className={`ml-2 mt-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${(p.attributes?.food_type || p.food_type) === "Vegetarian" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
              <span className={`h-2.5 w-2.5 rounded-sm border ${(p.attributes?.food_type || p.food_type) === "Vegetarian" ? "border-emerald-600 bg-emerald-500" : "border-rose-600 bg-rose-500"}`} />{p.attributes?.food_type || p.food_type}
            </span>
          )}
          {p.description && (() => {
            const isLong = p.description.split("\n").length > 6 || p.description.length > 320;
            return (
              <div className="mt-4" data-testid="product-description">
                <div className={!descExpanded && isLong ? "relative max-h-36 overflow-hidden" : "relative"}>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-slate-600">{p.description}</p>
                  {!descExpanded && isLong && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-slate-50 to-transparent" />}
                </div>
                {isLong && <button onClick={() => setDescExpanded((v) => !v)} data-testid="product-description-toggle" className="mt-1 text-sm font-semibold text-blue-600 hover:underline">{descExpanded ? "Read less" : "Read more"}</button>}
              </div>
            );
          })()}
          {p.highlights?.length > 0 && <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">{p.highlights.map((h, i) => <li key={`${h}-${i}`}>{h}</li>)}</ul>}
          {p.specs?.length > 0 && (
            <div className="mt-5" data-testid="product-specs">
              <p className="mb-2 text-sm font-semibold text-slate-800">Specifications</p>
              <dl className="grid grid-cols-1 gap-y-1.5 rounded-xl bg-slate-50 p-3 text-sm sm:grid-cols-2">
                {p.specs.map((s) => (
                  <div key={s.label} className="flex gap-2" data-testid={`spec-${s.label}`}>
                    <dt className="w-28 shrink-0 text-slate-500">{s.label}</dt>
                    <dd className="font-medium text-slate-800">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
          {showCustom && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="custom-modal" onClick={() => setShowCustom(false)}>
              <div className="w-full max-w-md rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-base font-bold text-slate-800">Request a customization</h3>
                <p className="mb-2 text-xs text-slate-500">Tell the seller what you'd like. They'll review and send you a price — you only pay once you accept it.</p>
                <textarea value={custReq} onChange={(e) => setCustReq(e.target.value)} data-testid="custom-requirements" rows={4} className="w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="e.g. Chocolate cake, 1kg, 'Happy Birthday Aisha', delivered Saturday" />
                <label className="mt-2 inline-flex cursor-pointer items-center gap-1 text-xs font-semibold text-violet-600"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadCustFile} data-testid="custom-file" className="hidden" />+ Attach a reference photo (max 10 MB)</label>
                {custFiles.length > 0 && <div className="mt-1 flex flex-wrap gap-2">{custFiles.map((f, i) => <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-0.5 text-xs">{f.name}<button onClick={() => setCustFiles((x) => x.filter((_, j) => j !== i))} className="text-rose-500">×</button></span>)}</div>}
                <div className="mt-3 flex justify-end gap-2">
                  <button onClick={() => setShowCustom(false)} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-600">Cancel</button>
                  <button onClick={submitCustom} data-testid="custom-submit" className="rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">Send request</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <div className="mx-auto max-w-5xl px-6 pb-4">
        <RecoRail path={`/marketplace/public/frequently-bought/${id}${locQS}`} rail="frequently-bought" title="Frequently Bought Together" subtitle="Real products often bought with this" layout="grid" />
        <RecoRail path={`/marketplace/public/similar/${id}${locQS}`} rail="similar" title="Similar Products" subtitle="More options you might like" layout="grid" />
      </div>
      <div className="mt-6"><ReviewSummary productId={id} /></div>
      <ReviewsSection productId={id} />
      {lightbox && lbHero && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" data-testid="product-image-lightbox" onClick={() => setLightbox(false)}>
          <button onClick={() => setLightbox(false)} data-testid="product-lightbox-close" className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition hover:bg-white/20"><X className="h-5 w-5" /></button>
          <img src={lbHero} alt={p.name} onClick={(e) => e.stopPropagation()} className="max-h-[90vh] max-w-[90vw] object-contain" data-testid="product-lightbox-image" />
          {lbGallery.length > 1 && (<>
            <button onClick={(e) => { e.stopPropagation(); lbStep(-1); }} data-testid="product-lightbox-prev" className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"><ChevronLeft className="h-6 w-6" /></button>
            <button onClick={(e) => { e.stopPropagation(); lbStep(1); }} data-testid="product-lightbox-next" className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white transition hover:bg-white/20"><ChevronRight className="h-6 w-6" /></button>
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-white" data-testid="product-lightbox-counter">{lbIdx + 1} / {lbGallery.length}</div>
          </>)}
        </div>
      )}
    </div>
  );
}
